import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { projectService } from '../services/api';
import { 
  Briefcase, 
  DollarSign, 
  Clock, 
  MapPin, 
  Search, 
  Filter,
  Calendar,
  Users,
  Star,
  Bookmark,
  ExternalLink,
  AlertCircle,
  Sparkles
} from 'lucide-react';

interface Project {
  id: string;
  title: string;
  description: string;
  category: string;
  skills: string[];
  budget: {
    type: 'hourly' | 'fixed';
    min: number;
    max: number;
  };
  timeline: string;
  location: string;
  experience_level: string;
  client: {
    name: string;
    rating?: number;
    projects_completed?: number;
  };
  posted_date: string;
  proposals_count: number;
  status: string;
  is_saved?: boolean;
}

const Projects: React.FC = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [budgetFilter, setBudgetFilter] = useState('');
  const [experienceFilter, setExperienceFilter] = useState('');

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    filterProjects();
  }, [projects, searchTerm, categoryFilter, budgetFilter, experienceFilter]);

  const loadProjects = async () => {
    try {
      setIsLoading(true);
      const data = await projectService.getAll();
      
      // Transform the data to match our interface
      const transformedProjects = data.map((project: any) => ({
        id: project.id,
        title: project.title,
        description: project.description,
        category: project.category,
        skills: project.skills || [],
        budget: project.budget,
        timeline: project.timeline,
        location: project.location,
        experience_level: project.experience_level,
        client: {
          name: project.client_name || 'Unknown Client',
          rating: 4.5, // Default rating for now
          projects_completed: 1, // Default count for now
        },
        posted_date: project.posted_date,
        proposals_count: project.proposals_count || 0,
        status: project.status || 'open',
        is_saved: false, // Default to false for now
      }));
      
      setProjects(transformedProjects);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load projects');
      console.error('Error loading projects:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filterProjects = () => {
    let filtered = [...projects];

    // Search by title, description, or skills
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(project =>
        project.title.toLowerCase().includes(searchLower) ||
        project.description.toLowerCase().includes(searchLower) ||
        project.skills.some(skill => skill.toLowerCase().includes(searchLower))
      );
    }

    // Filter by category
    if (categoryFilter) {
      filtered = filtered.filter(project => project.category === categoryFilter);
    }

    // Filter by budget
    if (budgetFilter) {
      const maxBudget = parseFloat(budgetFilter);
      if (!isNaN(maxBudget)) {
        filtered = filtered.filter(project => {
          const projectMaxBudget = project.budget.type === 'hourly' 
            ? project.budget.max * 40 * 4 // Estimate monthly cost for hourly
            : project.budget.max;
          return projectMaxBudget <= maxBudget;
        });
      }
    }

    // Filter by experience level
    if (experienceFilter) {
      filtered = filtered.filter(project => project.experience_level === experienceFilter);
    }

    setFilteredProjects(filtered);
  };

  const handleSaveProject = (projectId: string) => {
    setProjects(prev => prev.map(project => 
      project.id === projectId 
        ? { ...project, is_saved: !project.is_saved }
        : project
    ));
  };

  const formatBudget = (budget: Project['budget']) => {
    if (budget.type === 'hourly') {
      return `$${budget.min}-${budget.max}/hr`;
    } else {
      return `$${budget.min.toLocaleString()}-${budget.max.toLocaleString()}`;
    }
  };

  const formatTimeline = (timeline: string) => {
    const timelineMap: Record<string, string> = {
      'less_than_1_week': 'Less than 1 week',
      '1_to_2_weeks': '1-2 weeks',
      '2_to_4_weeks': '2-4 weeks',
      '1_to_3_months': '1-3 months',
      '3_to_6_months': '3-6 months',
      'more_than_6_months': '6+ months'
    };
    return timelineMap[timeline] || timeline;
  };

  const categories = [
    'Web Development',
    'Mobile Development',
    'Design & Creative',
    'Writing & Translation',
    'Digital Marketing',
    'Data Science & Analytics',
    'Engineering & Architecture',
    'Admin & Customer Support',
    'Legal',
    'Other'
  ];

  const experienceLevels = [
    { value: 'entry', label: 'Entry Level' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'expert', label: 'Expert' },
  ];

  // If user is a client, show access denied message
  if (user?.userType === 'client') {
    return (
      <div className="min-h-screen flex items-center justify-center relative">
        <div className="relative text-center max-w-md mx-auto px-4">
          <div className="glass-card rounded-xl p-8 border border-white/10">
            <AlertCircle size={48} className="mx-auto text-red-400 mb-4 sm:w-12 sm:h-12" />
            <h1 className="text-xl sm:text-2xl font-bold text-white mb-4">Access Denied</h1>
            <p className="text-sm sm:text-base text-white/70 mb-6">
              This page is only available for freelancers. Clients can post projects but cannot browse existing projects.
            </p>
            <Link
              to="/post-project"
              className="inline-flex items-center btn-modern px-4 py-2 rounded-md text-sm sm:text-base"
            >
              Post a Project
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center relative">
        <div className="relative text-center">
          <div className="glass-card rounded-xl p-8 border border-white/10">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary-400 mx-auto mb-4"></div>
            <p className="text-white/70 text-sm sm:text-base">Loading projects...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <div className="relative">
        {/* Header */}
        <div className="glass-dark border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
            <div className="text-center">
              <div className="flex items-center justify-center mb-4">
                <Sparkles className="text-secondary-400 mr-2" size={24} />
                <h1 className="text-2xl sm:text-3xl font-bold text-white">
                  Find Your Next Project
                </h1>
                <Sparkles className="text-secondary-400 ml-2" size={24} />
              </div>
              <p className="text-base sm:text-lg text-white/70">
                Browse and apply to projects that match your skills and interests
              </p>
              {user?.userType === 'freelancer' && (
                <div className="mt-4 p-3 glass-secondary border border-secondary-400/30 rounded-lg">
                  <p className="text-xs sm:text-sm text-secondary-400">
                    <strong>Note:</strong> Only projects matching your skills are shown. 
                    {userProfile?.skills && userProfile.skills.length > 0 ? (
                      <span> Your skills: {userProfile.skills.join(', ')}</span>
                    ) : (
                      <span> Update your profile to add skills and see more relevant projects.</span>
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          {/* Filters */}
          <div className="glass-card rounded-xl p-4 sm:p-6 mb-4 sm:mb-6 border border-white/10">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Search
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search projects..."
                    className="w-full pl-10 pr-3 py-2 rounded-md text-sm sm:text-base transition-all duration-300 border-white/20 focus:border-secondary-400/50"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Search size={16} className="absolute left-3 top-2.5 text-white/50" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Category
                </label>
                <select
                  className="w-full px-3 py-2 rounded-md text-sm sm:text-base transition-all duration-300 border-white/20 focus:border-secondary-400/50"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Max Budget
                </label>
                <input
                  type="number"
                  placeholder="e.g., 5000"
                  className="w-full px-3 py-2 rounded-md text-sm sm:text-base transition-all duration-300 border-white/20 focus:border-secondary-400/50"
                  value={budgetFilter}
                  onChange={(e) => setBudgetFilter(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Experience Level
                </label>
                <select
                  className="w-full px-3 py-2 rounded-md text-sm sm:text-base transition-all duration-300 border-white/20 focus:border-secondary-400/50"
                  value={experienceFilter}
                  onChange={(e) => setExperienceFilter(e.target.value)}
                >
                  <option value="">All Levels</option>
                  {experienceLevels.map(level => (
                    <option key={level.value} value={level.value}>{level.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setCategoryFilter('');
                    setBudgetFilter('');
                    setExperienceFilter('');
                  }}
                  className="w-full glass hover:bg-white/10 text-white/80 hover:text-white px-4 py-2 rounded-md transition-all duration-300 text-sm sm:text-base"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>

          {/* Results Header */}
          <div className="mb-4 sm:mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
              <h2 className="text-lg sm:text-xl font-semibold text-white">
                {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''} found
              </h2>
              <div className="flex items-center space-x-2 text-xs sm:text-sm text-white/60">
                <Filter size={14} className="sm:w-4 sm:h-4" />
                <span>Filtered results</span>
              </div>
            </div>
          </div>

          {/* Projects Grid */}
          {filteredProjects.length > 0 ? (
            <div className="space-y-4 sm:space-y-6">
              {filteredProjects.map((project) => (
                <div key={project.id} className="glass-card rounded-xl p-4 sm:p-6 border border-white/10 hover:border-secondary-400/30 glass-card-hover transition-all duration-300">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 mb-2">
                        <h3 className="text-base sm:text-lg font-semibold text-white mb-2 sm:mb-0">
                          <Link to={`/project/${project.id}`} className="hover:text-secondary-400 transition-colors">
                            {project.title}
                          </Link>
                        </h3>
                        <span className="px-2 py-1 text-xs rounded-full glass-secondary text-secondary-400 border border-secondary-400/30 self-start sm:self-auto">
                          {project.category}
                        </span>
                      </div>
                      <p className="text-sm sm:text-base text-white/70 line-clamp-2 mb-3">{project.description}</p>
                      
                      {/* Skills */}
                      <div className="flex flex-wrap gap-1 sm:gap-2 mb-4">
                        {project.skills.slice(0, 4).map((skill, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 text-xs rounded-full glass text-white/70 border border-white/20"
                          >
                            {skill}
                          </span>
                        ))}
                        {project.skills.length > 4 && (
                          <span className="px-2 py-1 text-xs rounded-full glass text-white/70 border border-white/20">
                            +{project.skills.length - 4} more
                          </span>
                        )}
                      </div>

                      {/* Project Details */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 text-xs sm:text-sm text-white/60">
                        <div className="flex items-center space-x-1">
                          <DollarSign size={12} className="sm:w-3.5 sm:h-3.5" />
                          <span className="truncate">{formatBudget(project.budget)}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Clock size={12} className="sm:w-3.5 sm:h-3.5" />
                          <span className="truncate">{formatTimeline(project.timeline)}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <MapPin size={12} className="sm:w-3.5 sm:h-3.5" />
                          <span className="capitalize truncate">{project.location}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Users size={12} className="sm:w-3.5 sm:h-3.5" />
                          <span className="truncate">{project.proposals_count} proposals</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-row lg:flex-col items-center lg:items-end space-x-4 lg:space-x-0 lg:space-y-3 mt-4 lg:mt-0 lg:ml-4">
                      <button
                        onClick={() => handleSaveProject(project.id)}
                        className={`p-2 rounded-full transition-all duration-300 ${
                          project.is_saved 
                            ? 'text-red-400 hover:text-red-300' 
                            : 'text-white/50 hover:text-white/70'
                        }`}
                      >
                        <Bookmark size={16} fill={project.is_saved ? 'currentColor' : 'none'} />
                      </button>
                      
                      <div className="text-center lg:text-right">
                        <div className="flex items-center justify-center lg:justify-start space-x-1 mb-1">
                          <Star size={12} className="sm:w-3.5 sm:h-3.5 text-secondary-400" />
                          <span className="text-xs sm:text-sm font-medium text-white">{project.client.rating}</span>
                        </div>
                        <p className="text-xs text-white/60">{project.client.name}</p>
                        <p className="text-xs text-white/60">{project.client.projects_completed} projects</p>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pt-4 border-t border-white/10 space-y-3 sm:space-y-0">
                    <div className="text-xs text-white/50 text-center sm:text-left">
                      Posted {new Date(project.posted_date).toLocaleDateString()}
                    </div>
                    <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                      <button className="px-3 sm:px-4 py-2 glass border border-secondary-400/30 text-secondary-400 hover:bg-secondary-400/10 rounded-md text-xs sm:text-sm font-medium transition-all duration-300">
                        View Details
                      </button>
                      <button className="px-3 sm:px-4 py-2 btn-modern rounded-md text-xs sm:text-sm font-medium transition-all duration-300">
                        Apply Now
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 sm:py-12">
              <div className="glass-card rounded-xl p-8 border border-white/10">
                <Briefcase size={40} className="mx-auto text-white/40 mb-4 sm:w-12 sm:h-12" />
                <h3 className="text-lg font-medium text-white mb-2">No projects found</h3>
                <p className="text-sm text-white/60">
                  {error ? error : 'Try adjusting your search criteria or filters.'}
                </p>
                {error && (
                  <button
                    onClick={loadProjects}
                    className="mt-4 btn-modern px-4 py-2 rounded-md text-sm sm:text-base"
                  >
                    Try Again
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Projects; 